import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User } from '@nzassa/shared';
import { ROLE_PERMISSIONS } from '@nzassa/shared';

interface AuthState {
  token: string | null;
  user: User | null;
  isAuthenticated: boolean;

  // Actions
  login: (token: string, user: User) => void;
  updateUser: (partial: Partial<User>) => void;
  logout: () => void;
  hasPermission: (permission: string) => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      user: null,
      isAuthenticated: false,

      login: (token, user) =>
        set({
          token,
          user,
          isAuthenticated: true,
        }),

      updateUser: (partial) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...partial } : null,
        })),

      logout: () =>
        set({
          token: null,
          user: null,
          isAuthenticated: false,
        }),

      hasPermission: (permission: string) => {
        const { user } = get();
        if (!user) return false;
        const perms = ROLE_PERMISSIONS[user.role] || [];
        return perms.includes('*') || perms.includes(permission);
      },
    }),
    {
      name: 'nzassa-auth',
      storage: typeof window !== 'undefined'
        ? {
            getItem: (k: string) => {
              const v = sessionStorage.getItem(k);
              return v ? JSON.parse(v) : null;
            },
            setItem: (k: string, v: unknown) => sessionStorage.setItem(k, JSON.stringify(v)),
            removeItem: (k: string) => sessionStorage.removeItem(k),
          }
        : undefined,
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }) as AuthState,
    },
  ),
);
